<template>
  <!--  todo move this into components / or move other dialogs from components into views-->

  <div v-if="!isLoading">
    <div class="mb-4">
      <h2 class="mb-1h" :style="color ? { color } : {}">
        {{ $t('consumables.heating.header') }}
      </h2>
      <div class="consumption-summary mb-2">
        <span class="large-text">{{ round(Number(heatingConsumable?.weekConsumption ?? 0), 2) }}</span>
        <span class="unit-text">kWh / {{ $t('consumables.heating.last7days') }}</span>
      </div>
      <ConsumptionHistoryLabels
        v-if="heatingConsumable?.dataSeries?.length"
        :data-series="heatingConsumable.dataSeries"
        :color="color"
      />
    </div>

    <div class="grid-wrapper">
      <div class="grid">
        <div class="icon">
          <Icon icon="aip-heating" :style="{ color }" size="3.5rem" />
        </div>
        <p>{{ $t('consumables.heating.totalTimeFromLastReset') }}</p>
        <div class="large">{{ heatingConsumable?.totalTimeFromLastReset }}</div>
      </div>
      <div class="grid">
        <div class="icon" />
        <p>{{ $t('consumables.heating.totalConsumption') }}</p>
        <div class="large">{{ heatingConsumable?.totalEnergyFromLastReset }}</div>
      </div>
      <div class="last-reset-grid">
        <p>{{ $t('consumables.heating.lastReset') }}</p>
        <p>{{ lastResetTime }}</p>
        <p>{{ lastResetDate }}</p>
      </div>
    </div>

    <div class="flex justify-end mb-1h mt-2h">
      <DialogComponent
        v-model="heatingResetDialog"
      >
        <template #activator>
          <BtnComponent
            white
            style="width: calc(40% + 1rem)"
            @click="heatingResetDialog = true"
          >
            {{ $t('common.reset') }}
          </BtnComponent>
        </template>
        <template #default>
          <h2>{{ $t('consumables.heating.confirmReset') }}</h2>
          <p class="mb-5">{{ $t('consumables.heating.confirmResetMessage') }}</p>
          <BtnComponent
            primary
            block
            large
            class="mb-1"
            :loading="isLoadingReset"
            :disabled="isLoadingReset"
            @click="resetHeating"
          >
            {{ $t('common.reset') }}
          </BtnComponent>
          <BtnComponent
            block
            outline
            large
            @click="heatingResetDialog = false"
          >
            {{ $t('common.cancel') }}
          </BtnComponent>
        </template>
      </DialogComponent>
    </div>

    <hr class="mb-2h">

    <h3>{{ $t('consumables.heating.configuration') }}</h3>
    <div class="flex align-center mb-1h">
      <h4 class="grow-1">{{ $t('consumables.heating.heatingPower') }}</h4>
      <SelectComponent
        v-model="updateItem.power"
        :options="powerOptions"
        :disabled="savingConfiguration"
        class="mb-0 mt-0"
        style="width: calc(40% + 1rem)"
      >
        <template #append>kW</template>
      </SelectComponent>
    </div>
    <div class="flex align-center justify-end mb-1h">
      <BtnComponent
        white
        style="width: calc(40% + 1rem)"
        :loading="savingConfiguration"
        @click="updateHeatingPower"
      >
        {{ $t('consumables.waterFilling.saveConfiguration') }}
      </BtnComponent>
    </div>
  </div>
  <div v-else>
    <BtnComponent loading block disabled large />
  </div>
</template>

<script>
import { ViewQuery, ResetHeatingMutation, UpdateHeatingPowerMutation } from '@acs/components-library/src/gql/live/ConsumableHeating.js';
import BtnComponent from '@acs/components-library/src/components/common/Btn.vue';
import DialogComponent from '@acs/components-library/src/components/common/Dialog.vue';
import SelectComponent from '@acs/components-library/src/components/common/Select.vue';
import ConsumptionHistoryLabels from '@acs/components-library/src/components/deviceDetail/diagrams/ConsumptionHistoryLabels.vue';
import Icon from '@acs/components-library/src/components/common/Icon.vue';
import { isDemo } from '@acs/components-library/src/demo.js';
import formatGrammaticalNumber from '@acs/components-library/src/helpers/gramaticalNumber.js';
import { round } from '@acs/components-library/src/helpers/math.js';
import { useToastStore } from '../store/toastStore.js';

export default {
  name: 'ConsumableHeatingDetail',
  components: {
    BtnComponent,
    DialogComponent,
    SelectComponent,
    ConsumptionHistoryLabels,
    Icon,
  },
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      sn: this.$route.params.sn,
      isDemo: isDemo(),
      isLoadingReset: false,
      heatingResetDialog: false,
      savingConfiguration: false,
      updateItem: {
        power: null,
      },
      powerOptions: Array.from({ length: 301 }, (_, i) => {
        const v = Number((i * 0.1).toFixed(1));
        return { value: v, text: v.toFixed(1) };
      }),
    };
  },
  apollo: {
    unitBySerialNumber: {
      query: ViewQuery,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          sn: this.sn,
        };
      },
    },
  },
  computed: {
    unit() {
      return this.unitBySerialNumber;
    },
    heatingConsumable() {
      return this.unit?.consumables?.find((c) => c?.__typename === 'HeatingConsumable') ?? null;
    },
    color() {
      return this.heatingConsumable?.color;
    },
    lastResetRaw() {
      return this.heatingConsumable?.lastReset ?? null;
    },
    lastResetTime() {
      if (!this.lastResetRaw || this.lastResetRaw === '---') return '---';
      return String(this.lastResetRaw).split(' ')[0] ?? '---';
    },
    lastResetDate() {
      if (!this.lastResetRaw || this.lastResetRaw === '---') return '---';
      const parts = String(this.lastResetRaw).split(' ');
      return parts.slice(1).join(' ') || '---';
    },
    isLoading() {
      return this.$apollo.loading || !this.unit;
    },
  },
  watch: {
    heatingConsumable: {
      immediate: true,
      handler(v) {
        if (v && v.power !== null && v.power !== undefined) {
          this.updateItem.power = v.power;
        }
      },
    },
  },
  methods: {
    round,
    formatGrammaticalNumber,
    async resetHeating() {
      if (this.isDemo) {
        this.toastStore.showSuccess(this.$t('consumables.heating.counterReset'));
        return;
      }

      this.isLoadingReset = true;
      const { data: { resetHeatingCounter } } = await this.$apollo.mutate({
        mutation: ResetHeatingMutation,
        variables: {
          serialNumber: this.sn,
        },
      }).finally(() => {
        this.isLoadingReset = false;
        this.heatingResetDialog = false;
      });

      // todo use common error message
      switch (resetHeatingCounter.__typename) {
        case 'Heating':
        case 'HeatingConsumable': // 🥳
          this.toastStore.showSuccess(this.$t('consumables.heating.counterReset'));
          await this.$apollo.queries.unitBySerialNumber.refetch();
          break;
        case 'HeatingNotAllowedError':
          this.toastStore.showError(this.$t('consumables.heating.notAllowed'));
          break;
        case 'UnitNotFoundError':
          this.toastStore.showError(this.$t('unit.unitNotFound'));
          break;
        case 'UnitNotPairedError':
          this.toastStore.showError(this.$t('unit.unitNotPaired'));
          break;
        default:
          this.toastStore.showError(this.$t('common.error.unknownResponseType', { type: resetHeatingCounter.__typename }));
          break;
      }
    },
    async updateHeatingPower() {
      if (this.isDemo) {
        this.toastStore.showSuccess(this.$t('common.successfullySaved'));
        return;
      }

      this.savingConfiguration = true;
      const power = Number(this.updateItem.power);
      const { data: { updateHeatingPower } } = await this.$apollo.mutate({
        mutation: UpdateHeatingPowerMutation,
        variables: {
          serialNumber: this.sn,
          power,
        },
      }).finally(() => {
        this.savingConfiguration = false;
      });

      switch (updateHeatingPower.__typename) {
        case 'Heating':
        case 'HeatingConsumable': // 🥳
          this.toastStore.showSuccess(this.$t('common.successfullySaved'));
          break;
        case 'HeatingNotAllowedError':
          this.toastStore.showError(this.$t('consumables.heating.notAllowed'));
          break;
        case 'UnitNotFoundError':
          this.toastStore.showError(this.$t('unit.unitNotFound'));
          break;
        case 'UnitNotPairedError':
          this.toastStore.showError(this.$t('unit.unitNotPaired'));
          break;
        default:
          this.toastStore.showError(this.$t('common.error.unknownResponseType', { type: updateHeatingPower.__typename }));
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.consumption-summary {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.grid-wrapper {
  padding-top: 1rem;
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 1rem;
}

.grid {
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: 2fr 3fr 2fr;
  row-gap: 0.5rem;
  column-gap: 2rem;
  align-items: center;
  justify-items: center;

  > :nth-child(3n+2) { justify-self: start; }
  > :nth-child(3n) { justify-self: end; }
}

.last-reset-grid {
  display: grid;
  grid-template-columns: 2fr 2fr 2fr;
  row-gap: 0.5rem;
  column-gap: 2rem;
  align-items: start;
  justify-items: end;
}

.icon {
  width: 5rem;
  height: 5rem;
  display: grid;
  place-items: center;
}

.large {
  font-size: 2rem;
  line-height: 2rem;
}

.large-text {
  font-size: 1.5rem;
}

.unit-text {
  font-size: 1rem;
}
</style>
